import { useState } from "react";
import { Alert, Button, Group, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconEye, IconLock } from "@tabler/icons-react";
import { Money, LocalMoney, Bps } from "@/components/Money";
import { Mono, ExternalMono } from "@/components/Mono";
import { RelatedLink } from "@/components/RelatedLink";
import { FieldRow } from "@/components/DetailGrid";
import { useAuth } from "@/auth/useAuth";
import { humanize } from "@/lib/format";
import { txUrl, addressUrl, baseTxUrl, baseAddressUrl } from "@/lib/explorer";
import { COLLECTIONS } from "@/firestore/collections";
import type {
  DepositDoc,
  ForwarderSweepDoc,
  OfframpBurnDoc,
  PaymentAccountDoc,
  VirtualAccountCredentials,
  VirtualAccountDoc,
  WithdrawalDoc,
  WithDocId,
} from "@/firestore/types";
import type { EntityConfig } from "../types";
import { monoCol, moneyCol, relatedCol, stateCol, textCol, timeCol } from "../shared";

type P<T> = WithDocId<T>;

// ─────────────────────────── Deposit (fiat → USDC) ───────────────────────────

export const depositConfig: EntityConfig<DepositDoc> = {
  key: "deposit",
  collection: COLLECTIONS.deposits,
  title: "Deposit",
  pluralTitle: "Deposits",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<DepositDoc>>("id", "ID", (r) => r.id),
    stateCol<P<DepositDoc>>((r) => r.state),
    textCol<P<DepositDoc>>("source", "Source", (r) => `${(r.sourceAmountMinor / 100).toFixed(2)} ${r.sourceCurrency}`),
    moneyCol<P<DepositDoc>>("dest", "Credited USDC", (r) => r.destinationAmountMicroUsdc),
    textCol<P<DepositDoc>>("provider", "Provider", (r) => r.provider, { humanize: true }),
    relatedCol<P<DepositDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<DepositDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Source amount", value: <LocalMoney minor={d.sourceAmountMinor} currency={d.sourceCurrency} /> },
    { label: "Credited USDC", value: <Money micro={d.destinationAmountMicroUsdc} fw={600} /> },
    { label: "Provider", value: humanize(d.provider) },
    { label: "Virtual account", value: <RelatedLink entity="virtualAccount" id={d.virtualAccount} /> },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.account} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Failure reason", value: d.failureReason ?? "—", full: true },
  ],
  related: (d) => [
    {
      // `ForwarderSweep.triggerRef` carries the deposit id for deposit-triggered sweeps, so this
      // is the hop from "Rain says it delivered the fiat" to "here is what happened on chain".
      // A deposit stuck in `processing` with NO sweep here means the webhook never landed and the
      // reconciliation cron has not yet swept the forwarder.
      title: "Forwarder sweeps (on-chain delivery)",
      entity: "forwarderSweep",
      collection: COLLECTIONS.forwarderSweeps,
      fkField: "triggerRef",
      value: d.id,
      orderBy: [["createdAt", "desc"]],
    },
  ],
};

// ─────────────────────────── Withdrawal (USDC → fiat) ───────────────────────────

export const withdrawalConfig: EntityConfig<WithdrawalDoc> = {
  key: "withdrawal",
  collection: COLLECTIONS.withdrawals,
  title: "Withdrawal",
  pluralTitle: "Withdrawals",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<WithdrawalDoc>>("id", "ID", (r) => r.id),
    stateCol<P<WithdrawalDoc>>((r) => r.state),
    moneyCol<P<WithdrawalDoc>>("source", "Source USDC", (r) => r.sourceAmountMicroUsdc),
    textCol<P<WithdrawalDoc>>("dest", "Paid out", (r) =>
      r.destinationAmountMinor !== null ? `${(r.destinationAmountMinor / 100).toFixed(2)} ${r.destinationCurrency}` : null,
    ),
    textCol<P<WithdrawalDoc>>("provider", "Provider", (r) => r.provider, { humanize: true }),
    // Where the money was actually sent. Worth a list column, not just a detail field: the
    // question "did this go to the right bank account?" is asked while scanning a user's
    // withdrawals, not after opening one.
    relatedCol<P<WithdrawalDoc>>("paymentAccount", "Payment account", "paymentAccount", (r) => r.paymentAccount),
    relatedCol<P<WithdrawalDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<WithdrawalDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Source USDC", value: <Money micro={d.sourceAmountMicroUsdc} fw={600} /> },
    { label: "Paid out", value: <LocalMoney minor={d.destinationAmountMinor} currency={d.destinationCurrency} /> },
    { label: "Realized FX", value: <Bps bps={d.realizedExchangeRateBps} /> },
    { label: "Provider", value: humanize(d.provider) },
    { label: "Payment account", value: <RelatedLink entity="paymentAccount" id={d.paymentAccount} /> },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.account} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Failure reason", value: d.failureReason ?? "—", full: true },
  ],
  related: (d) => [
    {
      // A withdrawal that sits in `burning` says nothing about why. The burn row carries the
      // UserOp, the attestation, the Base mint, and the reason for a `denied` / `failed`.
      title: "Off-ramp burn (on-chain)",
      entity: "offrampBurn",
      collection: COLLECTIONS.offrampBurns,
      fkField: "withdrawal",
      value: d.id,
    },
    {
      title: "Holds",
      entity: "hold",
      collection: COLLECTIONS.holds,
      fkField: "sourceWithdrawal",
      value: d.id,
    },
  ],
};

// ─────────────────────────── Virtual account (on-ramp) ───────────────────────────

/**
 * The banking credentials of an active virtual account, masked by default.
 *
 * These are admin-readable by Firestore rule, and the temptation is to just render them — they
 * are, after all, only the details a user wires money TO. They are still a full account +
 * routing number tied to a named individual, and an operator console is a screen-shared,
 * screen-recorded, over-the-shoulder environment. So: masked for every operator, revealable only
 * by `superAdmin`, and never a passive render.
 *
 * The reveal is client-side only — an `admin` already has read access to the underlying document
 * by rule, so this is a discipline against accidental disclosure, NOT an access control. Making
 * it a real one means moving credentials behind an admin endpoint with an audit trail.
 */
function CredentialsBlock({ credentials }: { credentials: VirtualAccountCredentials | null }) {
  const { capabilities } = useAuth();
  const [revealed, setRevealed] = useState(false);

  if (!credentials) {
    return (
      <Text c="dimmed" size="sm">
        — (credentials exist only while the account is active)
      </Text>
    );
  }

  const mask = (v: string) => (v.length <= 4 ? "••••" : `••••${v.slice(-4)}`);
  const show = revealed && capabilities.superAdmin;

  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="nowrap">
        <Text size="sm" c="dimmed">
          Banking credentials — the details the user wires to.
        </Text>
        {capabilities.superAdmin ? (
          <Button
            size="xs"
            variant="light"
            leftSection={show ? <IconLock size={14} /> : <IconEye size={14} />}
            onClick={() => setRevealed((r) => !r)}
          >
            {show ? "Hide" : "Reveal"}
          </Button>
        ) : (
          <Text size="xs" c="dimmed">
            <IconLock size={12} style={{ verticalAlign: "-2px" }} /> superAdmin required to reveal
          </Text>
        )}
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <FieldRow
          label="Account number"
          value={show ? <Mono value={credentials.accountNumber} /> : <Text ff="monospace" size="sm">{mask(credentials.accountNumber)}</Text>}
        />
        <FieldRow
          label="Routing number"
          value={show ? <Mono value={credentials.routingNumber} /> : <Text ff="monospace" size="sm">{mask(credentials.routingNumber)}</Text>}
        />
        <FieldRow label="Beneficiary" value={credentials.beneficiaryName} />
        <FieldRow label="Beneficiary bank" value={credentials.beneficiaryBankName} />
        <FieldRow
          label="Beneficiary address"
          value={show ? credentials.beneficiaryAddress : "••••"}
        />
        <FieldRow
          label="Bank address"
          value={show ? credentials.beneficiaryBankAddress : "••••"}
        />
        <FieldRow
          label="Transfer reference"
          value={credentials.transferMessage ? <Mono value={credentials.transferMessage} /> : "—"}
        />
      </SimpleGrid>
    </Stack>
  );
}

export const virtualAccountConfig: EntityConfig<VirtualAccountDoc> = {
  key: "virtualAccount",
  collection: COLLECTIONS.virtualAccounts,
  title: "Virtual Account",
  pluralTitle: "Virtual Accounts",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<VirtualAccountDoc>>("id", "ID", (r) => r.id),
    stateCol<P<VirtualAccountDoc>>((r) => r.state),
    textCol<P<VirtualAccountDoc>>("rail", "Rail", (r) => r.railHint, { humanize: true }),
    monoCol<P<VirtualAccountDoc>>("forwarder", "Forwarder (Base)", (r) => r.destinationAddress),
    monoCol<P<VirtualAccountDoc>>("route", "Rain route", (r) => r.providerRouteId),
    relatedCol<P<VirtualAccountDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<VirtualAccountDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Provider", value: humanize(d.provider) },
    {
      // Quote this in Rain support tickets — it is the id Rain's own console keys on.
      label: "Rain route id",
      value: <Mono value={d.providerRouteId} />,
    },
    { label: "Rail hint", value: humanize(d.railHint) },
    { label: "Destination rail", value: humanize(d.destinationRail) },
    {
      label: "Forwarder (Base)",
      value: <ExternalMono value={d.destinationAddress} href={baseAddressUrl(d.destinationAddress)} />,
    },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.account} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Failure reason", value: d.failureReason ?? "—", full: true },
    { label: "Credentials", value: <CredentialsBlock credentials={d.credentials} />, full: true },
  ],
  related: (d) => [
    {
      title: "Deposits",
      entity: "deposit",
      collection: COLLECTIONS.deposits,
      fkField: "virtualAccount",
      value: d.id,
      orderBy: [["createdAt", "desc"]],
    },
    {
      // Keyed on the forwarder address rather than the VA id — sweeps belong to the forwarder,
      // and one forwarder can be swept by triggers other than a deposit (reconciliation, or a
      // third party's burn adopted as an orphan).
      title: "Forwarder sweeps",
      entity: "forwarderSweep",
      collection: COLLECTIONS.forwarderSweeps,
      fkField: "forwarder",
      value: d.destinationAddress ?? undefined,
      orderBy: [["createdAt", "desc"]],
    },
  ],
  /**
   * The generic detail screen renders the whole document as raw JSON at the bottom, which would
   * defeat the masking above entirely. Strip the credentials from that view — the block is the
   * only surface for them.
   */
  redactRaw: (d) => ({ ...d, credentials: d.credentials ? "[redacted — see Credentials]" : null }),
};

// ─────────────────────────── Payment account (off-ramp) ───────────────────────────

export const paymentAccountConfig: EntityConfig<PaymentAccountDoc> = {
  key: "paymentAccount",
  collection: COLLECTIONS.paymentAccounts,
  title: "Payment Account",
  pluralTitle: "Payment Accounts",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<PaymentAccountDoc>>("id", "ID", (r) => r.id),
    stateCol<P<PaymentAccountDoc>>((r) => r.routeState),
    textCol<P<PaymentAccountDoc>>("nickname", "Nickname", (r) => r.nickname),
    textCol<P<PaymentAccountDoc>>("bank", "Bank", (r) => `${r.bankName} ••${r.lastFourAccount}`),
    textCol<P<PaymentAccountDoc>>("type", "Type", (r) => r.bankAccountType, { humanize: true }),
    relatedCol<P<PaymentAccountDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<PaymentAccountDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Nickname", value: d.nickname },
    { label: "Bank", value: `${d.bankName} ••${d.lastFourAccount}` },
    { label: "Account type", value: humanize(d.bankAccountType) },
    { label: "Beneficiary type", value: humanize(d.beneficiaryType) },
    { label: "Rain route id", value: <Mono value={d.providerRouteId} /> },
    {
      // Cross-check this against an off-ramp burn's `destinationAddressBase` when a withdrawal
      // is disputed: they must agree, and the burn refuses if they don't.
      label: "Route deposit address (Base)",
      value: <ExternalMono value={d.depositAddress} href={baseAddressUrl(d.depositAddress)} />,
    },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.account} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Route failure reason", value: d.routeFailureReason ?? "—", full: true },
  ],
  related: (d) => [
    {
      title: "Withdrawals",
      entity: "withdrawal",
      collection: COLLECTIONS.withdrawals,
      fkField: "paymentAccount",
      value: d.id,
      orderBy: [["createdAt", "desc"]],
    },
  ],
};

// ─────────────────────────── Forwarder sweep (on-ramp, on-chain) ───────────────────────────

/**
 * ⚠ Chain orientation here is the MIRROR of the settlement / off-ramp surfaces: the deploy and
 * the burn happen on **Base** and the mint lands on **HyperEVM**. Reusing `txUrl` for the burn
 * (as the settlement config correctly does for ITS burn) would point at the wrong explorer.
 */
export const forwarderSweepConfig: EntityConfig<ForwarderSweepDoc> = {
  key: "forwarderSweep",
  collection: COLLECTIONS.forwarderSweeps,
  title: "Forwarder Sweep",
  pluralTitle: "Forwarder Sweeps",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<ForwarderSweepDoc>>("id", "ID", (r) => r.id),
    stateCol<P<ForwarderSweepDoc>>((r) => r.state),
    moneyCol<P<ForwarderSweepDoc>>("amount", "Amount", (r) => r.amountMicroUsdc),
    textCol<P<ForwarderSweepDoc>>("trigger", "Trigger", (r) => r.trigger, { humanize: true }),
    monoCol<P<ForwarderSweepDoc>>("forwarder", "Forwarder", (r) => r.forwarder),
    monoCol<P<ForwarderSweepDoc>>("burn", "Burn tx (Base)", (r) => r.burnTxHash),
    monoCol<P<ForwarderSweepDoc>>("mint", "Mint tx (Hyper)", (r) => r.mintTxHash),
    timeCol<P<ForwarderSweepDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => (
    <Group gap="xs">
      <Mono value={d.id} />
      <Text size="sm" c="dimmed">
        {humanize(d.trigger)}
      </Text>
    </Group>
  ),
  detailFields: (d) => [
    {
      label: "Amount swept",
      value: (
        <Stack gap={2}>
          <Money micro={d.amountMicroUsdc} fw={600} />
          <Text size="xs" c="dimmed">
            the forwarder's balance at fire time — not necessarily one deposit
          </Text>
        </Stack>
      ),
    },
    { label: "Trigger", value: humanize(d.trigger) },
    {
      label: "Trigger ref",
      value: d.trigger === "deposit" ? <RelatedLink entity="deposit" id={d.triggerRef} /> : <Mono value={d.triggerRef} />,
    },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.wallet} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    {
      label: "Forwarder (Base)",
      value: <ExternalMono value={d.forwarder} href={baseAddressUrl(d.forwarder)} />,
    },
    {
      label: "Mint recipient (Main, HyperEVM)",
      value: <ExternalMono value={d.mintRecipient} href={addressUrl(d.mintRecipient)} />,
    },
    {
      label: "Deploy tx (Base)",
      value: <ExternalMono value={d.deployTxHash} href={baseTxUrl(d.deployTxHash)} />,
    },
    {
      label: "Burn tx — sweep() (Base)",
      value: <ExternalMono value={d.burnTxHash} href={baseTxUrl(d.burnTxHash)} />,
    },
    {
      label: "Mint tx (HyperEVM)",
      value: <ExternalMono value={d.mintTxHash} href={txUrl(d.mintTxHash)} />,
    },
    { label: "CCTP nonce", value: <Mono value={d.cctpNonce} short copy /> },
    { label: "Attestation", value: <Mono value={d.attestation} short copy /> },
    { label: "Status", value: <SweepNotice doc={d} />, full: true },
  ],
};

/**
 * The two sweep outcomes an operator will misread without help: a benign `abandoned`, and an
 * empty `mintTxHash` that actually means a third-party relayer got there first.
 */
function SweepNotice({ doc }: { doc: P<ForwarderSweepDoc> }) {
  if (doc.state === "abandoned") {
    return (
      <Alert color="gray" variant="light" title="Abandoned — not a failure">
        <Text size="sm">
          The forwarder's balance was below the sweep floor, so sweeping would have cost more gas
          than it delivered. The funds are safe on the forwarder and the next deposit will carry
          them. No action needed.
        </Text>
        {doc.failureReason ? (
          <Text size="xs" c="dimmed" mt="xs">
            {doc.failureReason}
          </Text>
        ) : null}
      </Alert>
    );
  }
  if (doc.state === "failed") {
    return (
      <Alert color="red" variant="light" title="Failed">
        <Text size="sm">{doc.failureReason ?? "No reason recorded."}</Text>
      </Alert>
    );
  }
  if ((doc.state === "minted" || doc.state === "credited") && !doc.mintTxHash) {
    return (
      <Alert color="green" variant="light" title="Delivered by a third-party relayer">
        <Text size="sm">
          No mint tx of ours: `receiveMessage` is permissionless and someone else submitted it
          first. The USDC still arrived in the user's Main — the destination is baked into the
          signed CCTP message and cannot be redirected.
        </Text>
      </Alert>
    );
  }
  return (
    <Text size="sm" c="dimmed">
      {doc.failureReason ?? "—"}
    </Text>
  );
}

// ─────────────────────────── Off-ramp burn (off-chain → bank) ───────────────────────────

export const offrampBurnConfig: EntityConfig<OfframpBurnDoc> = {
  key: "offrampBurn",
  collection: COLLECTIONS.offrampBurns,
  title: "Off-Ramp Burn",
  pluralTitle: "Off-Ramp Burns",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<OfframpBurnDoc>>("id", "ID", (r) => r.id),
    stateCol<P<OfframpBurnDoc>>((r) => r.state),
    moneyCol<P<OfframpBurnDoc>>("amount", "Amount", (r) => r.amountMicroUsdc),
    relatedCol<P<OfframpBurnDoc>>("withdrawal", "Withdrawal", "withdrawal", (r) => r.withdrawal),
    monoCol<P<OfframpBurnDoc>>("burn", "Burn tx (Hyper)", (r) => r.burnTxHash),
    monoCol<P<OfframpBurnDoc>>("mint", "Mint tx (Base)", (r) => r.mintTxHash),
    relatedCol<P<OfframpBurnDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<OfframpBurnDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Amount burned", value: <Money micro={d.amountMicroUsdc} fw={600} /> },
    { label: "Withdrawal", value: <RelatedLink entity="withdrawal" id={d.withdrawal} /> },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.wallet} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    {
      // Surfaced because it is the proof the user authorized this: the key is one-shot and
      // biometric-installed, and the backend cannot mint one.
      label: "Session key (one-shot, biometric)",
      value: <RelatedLink entity="sessionKey" id={d.sessionKey} />,
    },
    { label: "Source SAO", value: <RelatedLink entity="smartAccountOperation" id={d.sourceSmartAccountOperation} /> },
    {
      label: "Destination — Rain route (Base)",
      value: <ExternalMono value={d.destinationAddressBase} href={baseAddressUrl(d.destinationAddressBase)} />,
    },
    {
      label: "Burn tx (HyperEVM)",
      value: <ExternalMono value={d.burnTxHash} href={txUrl(d.burnTxHash)} />,
    },
    {
      label: "Mint tx (Base)",
      value: <ExternalMono value={d.mintTxHash} href={baseTxUrl(d.mintTxHash)} />,
    },
    { label: "CCTP nonce", value: <Mono value={d.cctpNonce} short copy /> },
    { label: "Attestation", value: <Mono value={d.attestation} short copy /> },
    { label: "Status", value: <BurnNotice doc={d} />, full: true },
  ],
};

/** `denied` and `delivered` both read as "done" without context, and mean opposite things. */
function BurnNotice({ doc }: { doc: P<OfframpBurnDoc> }) {
  if (doc.state === "denied") {
    return (
      <Alert color="orange" variant="light" title="Denied — expected, not an incident">
        <Text size="sm">
          The wallet had insufficient spendable balance when the hold was taken. This is the
          authoritative balance check for the whole off-ramp — the withdrawal endpoint cannot
          perform one, and the mobile pre-check is advisory. The user can retry with a smaller
          amount.
        </Text>
        {doc.failureReason ? (
          <Text size="xs" c="dimmed" mt="xs">
            {doc.failureReason}
          </Text>
        ) : null}
      </Alert>
    );
  }
  if (doc.state === "failed") {
    return (
      <Alert color="red" variant="light" title="Failed — needs a human">
        <Text size="sm">{doc.failureReason ?? "No reason recorded."}</Text>
      </Alert>
    );
  }
  if (doc.state === "delivered") {
    return (
      <Alert color="green" variant="light" title="Delivered to Rain — not yet in the user's bank">
        <Text size="sm">
          The USDC reached Rain's route on Base. Rain still has to convert and pay out; that
          progress lives on the withdrawal's own <code>processing → completed</code>.
        </Text>
      </Alert>
    );
  }
  return (
    <Text size="sm" c="dimmed">
      {doc.failureReason ?? "—"}
    </Text>
  );
}
